import {
  BreakpointsPx,
  colors,
  Icons,
  ImpContainer,
  ImpFlex,
  ImpHidden,
  ImpIcon,
  ImpPad,
  ImpProgressBar,
  ImpSpacer,
  ImpText,
  useProgressBar,
} from '@imprint-payments/imprint-ui';
import { useStatsigClient } from '@statsig/react-bindings';
import { useEffect, useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import {
  MODAL_ENUM,
  setShowLinkHHScreen,
  setShowModal,
} from '../../app/applicationScreenSlice';
import { useImpState } from '../../app/hooks';
import { setLanguage } from '../../app/languageSlice';
import { ANALYTICS } from '../../firebase/firebase';
import { storeReferralCodeFromParams } from '../../lib/referralCodeManager';
import { setInStoreParams, setUtmSource } from '../../lib/utmManager';
import Gates from '../../statsig/gates';
import { setSessionLanguage } from '../../utils/functionUtils';
import { LanguageCode } from '../../utils/language';
import { SESSION_STORAGE } from '../../utils/sessionStorageConstants';
import DisclosureFooter from '../DisclosureFooter/DisclosureFooter';
import IncomeHousingV2 from '../incomeHousingID/IncomeHousingV2';
import LinkHHAccount from '../linkHHAccount/LinkHHAccount';
import PersonalInfo from '../personalinfo/PersonalInfo';
import PrefillFlow from '../prove/PrefillFlow';
import ProveContextProvider from '../prove/ProveContext';
import SelectionModal from '../selectionModal/SelectionModal';
import styles from './Application.module.css';
import InStoreModal from './InStoreModal';
import { RewardsDisplay } from './RewardsDisplay';

// order matters, we'll use this as index
enum Step {
  Prefill,
  Personal,
  Finances,
  End,
}

const config = [Step.Prefill, Step.Personal, Step.Finances].map((_, i) => ({
  label: i,
}));

function ProgressBar({
  step,
  backClick,
}: {
  step: number;
  backClick?: () => void;
}) {
  return (
    <ImpContainer height={'56px'} width={'100%'}>
      <ImpPad
        paddingTop={'s'}
        paddingBottom={'s'}
        paddingLeft={'m'}
        paddingRight={'m'}
        fluid
      >
        <ImpFlex
          fluid
          direction={'row'}
          align={'center'}
          justify={'space-between'}
        >
          <div style={{ flex: 1 }}>
            {backClick && (
              <ImpIcon
                ariaLabel="Go back to previous step"
                iconSrc={Icons.chevronLeft}
                onClick={backClick}
              />
            )}
          </div>
          <ImpContainer width={'100%'} maxWidth={'320px'}>
            <ImpProgressBar
              config={config}
              currentMainStep={step}
              checkmarkOnComplete
            />
          </ImpContainer>
          <div style={{ flex: 1 }} />
        </ImpFlex>
      </ImpPad>
    </ImpContainer>
  );
}

function ApplicationBody() {
  const { appStatus, language, appScreenState, dispatch } = useImpState();
  const { currentMainStep, moveForward, moveBackward } = useProgressBar(
    config,
    appStatus.reApplicationInApp ? Step.Finances : Step.Prefill
  );
  // hoisted state from IncomeHousingV2, needed to control back button in progressbar
  const [submitButtonState, setSubmitButtonState] = useState<
    'enabled' | 'disabled' | 'submitting'
  >('disabled');

  // if you're in reapplication, you must've passed personal info before
  const [passedPersonalInfo, setPassedPersonalInfo] = useState(
    !appStatus.reApplicationInApp
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    return () => {
      if (currentMainStep === Step.Personal) {
        setPassedPersonalInfo(true);
      }
    };
  }, [currentMainStep]);

  return (
    <ImpContainer>
      <ImpPad fluid padding="0px 24px" mobilePadding="0px 16px">
        <ProgressBar
          step={currentMainStep}
          backClick={
            currentMainStep === Step.Finances &&
            submitButtonState !== 'submitting'
              ? appScreenState.showLinkHHScreen
                ? () => dispatch(setShowLinkHHScreen(false))
                : moveBackward
              : undefined
          }
        />
      </ImpPad>

      <ImpContainer
        borderTop={`1px solid ${colors.border.borderVariant}`}
        borderBottom={`1px solid ${colors.border.borderVariant}`}
        width="100%"
      >
        <ImpPad padding="8px 4px">
          <ImpContainer textAlign={'center'}>
            <ImpText
              typography={'body2'}
              color={colors.content.onBackgroundVariant}
            >
              {language.personalInfoNotice}
            </ImpText>
          </ImpContainer>
        </ImpPad>
      </ImpContainer>

      <ImpPad padding="0px 24px" mobilePadding="0px 16px" fluid>
        <ImpFlex fluid justify="center">
          {
            {
              [Step.Prefill]: (
                <ProveContextProvider>
                  <PrefillFlow nextStep={moveForward} />
                </ProveContextProvider>
              ),
              [Step.Personal]: (
                <PersonalInfo
                  nextStep={moveForward}
                  repeatViewing={passedPersonalInfo}
                />
              ),
              [Step.Finances]: appScreenState.showLinkHHScreen ? (
                <LinkHHAccount />
              ) : (
                <IncomeHousingV2
                  submitButtonState={submitButtonState}
                  setSubmitButtonState={setSubmitButtonState}
                />
              ),
            }[currentMainStep]
          }
        </ImpFlex>
      </ImpPad>
    </ImpContainer>
  );
}

export default function Application() {
  const { language, merchantData, appScreenState, apiScreenState, dispatch } =
    useImpState();
  const { checkGate } = useStatsigClient();

  // workaround for session storage being unreliable. Proactively stuff it in local storage for safer keeping
  const utmSource = sessionStorage.getItem(SESSION_STORAGE.UTM_SOURCE);

  if (utmSource) {
    setUtmSource(merchantData.productUUID, utmSource);
  }

  setInStoreParams(merchantData.productUUID);

  if (checkGate(Gates.ReferralsEnabled)) {
    storeReferralCodeFromParams(merchantData.productUUID);
  }

  const switchToSpanish = () => {
    setSessionLanguage(LanguageCode.Spanish, apiScreenState.env);
    dispatch(setLanguage(LanguageCode.Spanish));
    dispatch(setShowModal(MODAL_ENUM.noModal));
  };

  const renderSetLanguageDisclaimerModal = () => {
    return (
      <SelectionModal
        header={language.areYouSure}
        body={language.switchToSpanish}
        handleCloseBtn={() => {
          dispatch(setShowModal(MODAL_ENUM.noModal));
          ANALYTICS.logEvent(EVENT.LANGUAGE_CANCEL_CLICKED, {
            page: 'language_selection_personal_information',
            language: LanguageCode.English,
          });
        }}
        buttonName1={language.switchToSpanishButton}
        button1Click={() => {
          switchToSpanish();
          ANALYTICS.logEvent(EVENT.LANGUAGE_CONTINUE_CLICKED, {
            page: 'language_selection_personal_information',
            language: LanguageCode.Spanish,
          });
        }}
        pillBtn1={true}
      />
    );
  };

  return (
    <>
      <div className={styles.container}>
        {appScreenState.showModal === MODAL_ENUM.showLanguageDisclaimerModal &&
          renderSetLanguageDisclaimerModal()}
        {appScreenState.inStoreDevice && !appScreenState.requireFullSSN && (
          <InStoreModal sourcePage="signup" />
        )}

        <div className={styles.contentContainer}>
          <ImpFlex justify="center" align="flex-start" flexWrap>
            <ImpContainer maxWidth="600px" mobileWidth="100%">
              <ImpFlex direction="column" align="center">
                <ImpHidden
                  hideAbove={BreakpointsPx.Application}
                  hideBelow={BreakpointsPx.Mobile}
                >
                  <ImpSpacer height="2xl" />
                </ImpHidden>
                <ImpContainer
                  width="532px"
                  mobileWidth="100%"
                  backgroundColor={colors.surface.surfaceLow}
                  borderRadius="12px"
                  mobileBorderRadius="0px"
                  border={`1px solid ${colors.border.borderVariant}`}
                  mobileBorder="none"
                >
                  <ApplicationBody />
                </ImpContainer>
                <ImpHidden
                  hideAbove={BreakpointsPx.Application}
                  hideBelow={BreakpointsPx.Mobile}
                >
                  <ImpSpacer height="2xl" />
                </ImpHidden>
              </ImpFlex>
            </ImpContainer>
            <ImpHidden hideBelow={BreakpointsPx.Application}>
              <ImpFlex>
                <ImpSpacer width="l" />
                <RewardsDisplay />
              </ImpFlex>
            </ImpHidden>
          </ImpFlex>
          <div className={styles.disclosureContainer}>
            <DisclosureFooter />
            <ImpSpacer height="m" />
          </div>
        </div>
      </div>
    </>
  );
}
